/**
 * Detect known in-app browser webviews from a user agent string.
 *
 * In-app browsers (GSA WebView, Facebook, Instagram, Telegram, etc.) do not
 * reliably expose the camera to embedded SDKs (SumSub KYC, in particular).
 * Sentry CUSTOMER-UI-36 shows iPhone users in the Google Search in-app
 * browser failing with `NotReadableError` during KYC verification.
 *
 * Use this to:
 *   - render the amber `<InAppBrowserWarning />` on registration / welcome
 *     pages so users can switch browsers before they hit the KYC flow
 *   - drop the noisy NotReadableError Sentry capture in `instrumentation-client.ts`
 *     when the UA is a known in-app webview (not actionable signal)
 *
 * SSR-safe: returns `false` when `navigator` is undefined.
 */
const IN_APP_BROWSER_PATTERNS: RegExp[] = [
  /\bGSA\//, // Google Search app
  /\bFBAN\/|\bFBAV\//, // Facebook
  /\bInstagram\b/, // Instagram
  /\bLine\//, // LINE
  /\bTwitter\b|\bX-Mobile\b/, // Twitter / X
  /\bTelegram\b/, // Telegram
  /\bSnapchat\b/, // Snapchat
  /\bTikTok\b/, // TikTok
  /\bWeChat\b|\bMicroMessenger/, // WeChat
  /\bwv\)/, // Android WebView
  /\bGoogle\/\d/, // Google Search WebView (Sentry CUSTOMER-UI-36 case)
];

export function isInAppBrowser(userAgent?: string): boolean {
  const ua =
    userAgent ??
    (typeof navigator === "undefined" ? undefined : navigator.userAgent);
  if (!ua) return false;
  return IN_APP_BROWSER_PATTERNS.some((pattern) => pattern.test(ua));
}
